import type { Rect } from './geometry';

/**
 * Uniform grid spatial index. Good enough for tens of thousands of items;
 * swap for an R-tree if boards get bigger than that.
 */
export class SpatialIndex {
  private cells = new Map<string, Set<string>>();
  private placed = new Map<string, string[]>();

  constructor(private readonly cellSize = 512) {}

  private keysFor(r: Rect): string[] {
    const s = this.cellSize;
    const x0 = Math.floor(r.x / s);
    const y0 = Math.floor(r.y / s);
    const x1 = Math.floor((r.x + r.w) / s);
    const y1 = Math.floor((r.y + r.h) / s);
    const keys: string[] = [];
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        keys.push(x + ':' + y);
      }
    }
    return keys;
  }

  set(id: string, bounds: Rect): void {
    this.remove(id);
    const keys = this.keysFor(bounds);
    for (const k of keys) {
      let cell = this.cells.get(k);
      if (!cell) {
        cell = new Set();
        this.cells.set(k, cell);
      }
      cell.add(id);
    }
    this.placed.set(id, keys);
  }

  remove(id: string): void {
    const keys = this.placed.get(id);
    if (!keys) return;
    for (const k of keys) {
      const cell = this.cells.get(k);
      if (!cell) continue;
      cell.delete(id);
      if (cell.size === 0) this.cells.delete(k);
    }
    this.placed.delete(id);
  }

  /** Candidate ids whose cells overlap the rect. Callers should re-check bounds if they need exactness. */
  query(r: Rect): Set<string> {
    const out = new Set<string>();
    for (const k of this.keysFor(r)) {
      const cell = this.cells.get(k);
      if (!cell) continue;
      for (const id of cell) out.add(id);
    }
    return out;
  }
}
